import { type FC, type FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router";

import { faEnvelope as faEmail } from "@fortawesome/pro-duotone-svg-icons/faEnvelope";
import { faKey as faPassword } from "@fortawesome/pro-duotone-svg-icons/faKey";

import { TURNSTILE_SITE_KEY } from "@application/config/turnstile";

import { useAuthenticated } from "@application/hooks/use_authenticated";
import { useLogin } from "@application/hooks/use_login";
import { useTrackPageView } from "@application/hooks/use_track_page_view";

import { track } from "@application/utilities/track";

import { AuthLayout } from "@layouts/auth_layout";

import { BasicLink } from "@styled/basic_link";
import { Button } from "@styled/button";
import { Card } from "@styled/card";
import { Form } from "@styled/form";
import { InputField } from "@styled/input_field";
import { Notification } from "@styled/notification";
import { Sentence } from "@styled/sentence";
import { Social } from "@styled/social";
import { Turnstile } from "@styled/turnstile";

const SignupLink: FC = () => <BasicLink to="/signup">Signup</BasicLink>;
const ResetLink: FC = () => <BasicLink to="/reset">Reset</BasicLink>;

export const Login: FC = () => {
  useTrackPageView({ page: "login" });

  const authenticated = useAuthenticated();
  const location = useLocation();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [turnstile, onTurnstile] = useState<string | undefined>(undefined);

  const { login, loading, errors } = useLogin();

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    track("login", { email });
    login({ email, password, turnstile });
  };

  if (authenticated) {
    const params = new URLSearchParams(location.search);
    return <Navigate to={params.get("back") ?? "/dashboard"} />;
  }

  return (
    <>
      <Social
        title="Login | Workflow"
        description="Join today and get started with automating your next workflow."
        url="https://workflow.ing/login"
      />

      <AuthLayout>
        <Card>
          <Form onSubmit={onSubmit}>
            {errors?.base && (
              <Notification color="rose">
                <Sentence>{errors.base}</Sentence>
              </Notification>
            )}

            <InputField
              id="email"
              name="email"
              type="email"
              label="Email:"
              placeholder="me@workflow.ing"
              icon={faEmail}
              errors={errors}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            <InputField
              id="password"
              name="password"
              type="password"
              label="Password:"
              placeholder="abc$123!"
              icon={faPassword}
              errors={errors}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            <Turnstile sitekey={TURNSTILE_SITE_KEY} onTokenize={onTurnstile} />

            <Button full loading={loading} type="submit">
              Login
            </Button>
          </Form>

          <p className="text-center text-slate-600">
            Need an account? <SignupLink />
          </p>

          <p className="text-center text-slate-600">
            Forgot your password? <ResetLink />
          </p>
        </Card>
      </AuthLayout>
    </>
  );
};
